import React, { useEffect, useState } from 'react';
import {
  Expandable, TextField, Toggle, Pill, Select, Button, Tabs, Tab, TabPanel,
} from '@warp-ds/react';
import { toast } from '@warp-ds/elements';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import axiosClient from '../axiosClient';
import {
  JobPriceInfoResponse, JobPriceResponse, Occupation, ToggleEntry,
} from '../../../types';
import JobAdModelInfo from './JobAdModelInfo';
import { fixToPositiveIntegerString } from '../../utils/priceUtil';
import SkeletonJobAdModelInfo from './SkeletonJobAdModelInfo';

type JobPriceOverviewProps = {
  orgId: number | undefined,
};

function JobPriceOverview({ orgId }: JobPriceOverviewProps) {
  const JOB_FULL_TIME = 'job-full-time';
  const JOB_PART_TIME = 'job-part-time';
  const JOB_AGENCY_FULL_TIME = 'job-agency-full-time';

  const [adModelNumberOfPublishedAds, setAdModelNumberOfPublishedAds] = useState<number>(0);
  const [occupations, setOccupations] = useState<Occupation[]>([]);
  const [selectedOccupations, setSelectedOccupations] = useState<Occupation[]>([]);
  const [segment, setSegment] = useState<String>('');
  const [jobPrices, setJobPrices] = useState<JobPriceResponse>();
  const [adModelTab, setAdModelTab] = useState<string>(JOB_FULL_TIME);
  const [fetchingPrices, setFetchingPrices] = useState<boolean>(false);
  const [adModelInput, setAdModelInput] = useState<string>('');
  const [numberOfPublishedAdsInput, setNumberOfPublishedAdsInput] = useState<string>('');
  const [jobPriceInfo, setJobPriceInfo] = useState<JobPriceInfoResponse>();
  const [jobAdText, setJobAdText] = useState<string>('annonser');
  const [priceCalcNextVolume, setPriceCalcNextVolume] = useState<number>(0);
  const [occupationError, setOccupationError] = useState<boolean>(false);
  const [numberOfPublishedAdsErrorMessage, setNumberOfPublishedAdsErrorMessage] = useState<string>('');
  const [showOccupationDropDown, setShowOccupationDropDown] = useState<boolean>(true);

  const options:ToggleEntry[] = [
    { label: 'Heltid/leder', value: JOB_FULL_TIME },
    { label: 'Deltid', value: JOB_PART_TIME },
    ...(jobPriceInfo?.isStaffing ? [{ label: 'Bemanning', value: JOB_AGENCY_FULL_TIME }] : []),
  ];
  const [selectedAdModelRadio, setSelectedAdModelRadio] = useState<ToggleEntry[]>([options[0]]);

  const isLoggedIn = orgId !== undefined;

  const getAdModelNumberOfPublishedAds = (adModel: string, data: JobPriceInfoResponse): number => data?.jobProductInfoGroups.find((productInfo) => productInfo.adModel === adModel)?.numberOfPublishedAds || 0;

  const setDefaultValues = (data: JobPriceInfoResponse) => {
    const defaultAdModel = JOB_FULL_TIME;
    const defaultAdModelNumberOfPublishedAds = getAdModelNumberOfPublishedAds(defaultAdModel, data);
    setNumberOfPublishedAdsInput(defaultAdModelNumberOfPublishedAds.toString());
    setAdModelNumberOfPublishedAds(defaultAdModelNumberOfPublishedAds);
    setAdModelInput(defaultAdModel);
  };

  function getJobInfo() {
    axiosClient.get('api/job-overview', {
      params: {
        orgId,
      },
    })
      .then((response) => response.data)
      .then((data) => {
        setJobPriceInfo(data);
        setDefaultValues(data);
      }).catch(() => {
        toast('Det har oppstått en feil ved henting av prisene. Prøv å oppdatere siden.', { type: 'error' });
      });
  }

  function getOccupations() {
    axiosClient.get('api/occupations')
      .then((response) => response.data)
      .then((data) => {
        setOccupations(data);
      }).catch(() => {
        toast('Det har oppstått en feil ved henting av stillinger. Prøv å oppdatere siden.', { type: 'error' });
      });
  }

  function getSegment(occupationIds: number[]) {
    axiosClient.get('api/segment', {
      params: {
        occupations: occupationIds,
      },
    })
      .then(({ data }) => setSegment(data))
      .catch(() => {
        toast('Det har oppstått en feil ved henting av stillinger. Prøv å oppdatere siden.', { type: 'error' });
      });
  }

  useEffect(() => {
    getOccupations();
    getJobInfo();
  }, []);

  useEffect(() => {
    if (selectedOccupations.length > 0) {
      getSegment(selectedOccupations.map((occ) => occ.id));
    }
  }, [selectedOccupations]);

  function getJobPrices() {
    const emptyOccupations = adModelInput !== JOB_AGENCY_FULL_TIME && selectedOccupations.length === 0;
    setFetchingPrices(true);
    setOccupationError(emptyOccupations);
    setNumberOfPublishedAdsErrorMessage(numberOfPublishedAdsInput === '' ? 'Du må fylle inn antall annonser' : '');
    if (emptyOccupations || numberOfPublishedAdsInput === '') {
      setFetchingPrices(false);
      return;
    }
    axiosClient.get('/api/job-prices/', {
      params: {
        segment,
        adModel: adModelInput,
        numberOfPublishedAds: numberOfPublishedAdsInput,
        orgId,
      },
    })
      .then((response) => response.data)
      .then((data) => {
        setJobPrices(data);
        setFetchingPrices(false);
        setPriceCalcNextVolume(parseInt(numberOfPublishedAdsInput, 10) + 1);
      }).catch(() => {
        setFetchingPrices(false);
        toast('Det har oppstått en feil ved henting av prisene. Vennligst prøv på nytt.', { type: 'error' });
      });
  }

  const selectOccupation = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (selectedOccupations.length < 3 && event.target.value !== '') {
      setSelectedOccupations((selected) => [...selected, occupations.find((o) => o.id.toString() === event.target.value)!]);
    }
  };

  const removeOccupation = (clickedOccupation: Occupation) => {
    setSelectedOccupations((prevOccupations) => prevOccupations.filter((occupation) => occupation !== clickedOccupation));
  };

  const onAdTypeChange = (adModel: string) => {
    setSelectedAdModelRadio([options.find((option) => option.value === adModel)!]);
    setAdModelInput(adModel);
    setAdModelNumberOfPublishedAds(jobPriceInfo?.jobProductInfoGroups.find((productInfo) => productInfo.adModel === adModel)?.numberOfPublishedAds || 0);
    setNumberOfPublishedAdsInput(getAdModelNumberOfPublishedAds(adModel, jobPriceInfo!).toString());
    if (adModel.includes('job-agency')) {
      setJobAdText('bemanningsannonser');
      setShowOccupationDropDown(false);
    } else {
      setJobAdText('annonser');
      setShowOccupationDropDown(true);
    }
  };

  const onTabClick = (adModel: string) => {
    setAdModelTab(adModel);
    onAdTypeChange(adModel);
  };

  return (
    <div>
      <p className="h2">Jobb</p>
      <Tabs>
        <Tab label="Heltid- og lederstilling" name={JOB_FULL_TIME} isActive onClick={() => onTabClick(JOB_FULL_TIME)} />
        <Tab label="Deltidstilling" name={JOB_PART_TIME} onClick={() => onTabClick(JOB_PART_TIME)} />
        {jobPriceInfo?.isStaffing && <Tab label="Bemanningstilling" name={JOB_AGENCY_FULL_TIME} onClick={() => onTabClick(JOB_AGENCY_FULL_TIME)} />}
      </Tabs>
      {jobPriceInfo
        ? (
          <div>
            <TabPanel name={JOB_FULL_TIME}>
              { jobPriceInfo ? (<JobAdModelInfo jobProductInfoGroup={jobPriceInfo?.jobProductInfoGroups.find((productInfo) => productInfo.adModel === JOB_FULL_TIME)} isLoggedIn={isLoggedIn} isStaffing={false} />) : (<Skeleton borderRadius="12px" width={350} height={600} className="p-24 rounded-8 m-20" />)}
            </TabPanel>
            <TabPanel name={JOB_PART_TIME}>
              <JobAdModelInfo jobProductInfoGroup={jobPriceInfo?.jobProductInfoGroups.find((productInfo) => productInfo.adModel === JOB_PART_TIME)} isLoggedIn={isLoggedIn} isStaffing={false} />
            </TabPanel>
            <TabPanel name={JOB_AGENCY_FULL_TIME}><JobAdModelInfo jobProductInfoGroup={jobPriceInfo?.jobProductInfoGroups.find((productInfo) => productInfo.adModel === JOB_AGENCY_FULL_TIME)} isLoggedIn={isLoggedIn} isStaffing /></TabPanel>
          </div>
        ) : (
          <SkeletonJobAdModelInfo />
        )}
      <Expandable
        title={(
          <div className="font-bold">
            Hvorfor er prisen forskjellig?
          </div>
          )}
        className="my-8"
        box
        animated
      >
        { adModelTab !== JOB_AGENCY_FULL_TIME ? (
          <div className="sm:pr-32">
            <p>Noen stillinger er enklere å rekruttere til, og andre vanskeligere. For stillingsannonser det er enklere å få tak i kandidater gir vi deg en lavere pris. På den andre siden jobber vi hardt for å tiltrekke kandidater i de krevende kategoriene, derfor er annonseprisen i disse kategoriene høyere.</p>
            <br />
            <p>I tillegg skal det lønne seg å bruke oss ofte. Derfor får du rabatt fra din ellevte annonse. Jo flere annonser du legger ut, desto lavere blir prisen.</p>
            <br />
            <p>Prisen er dynamisk og varierer ut fra annonsekategori og antall publiserte annonser de siste 365 dagene.</p>
            <br />
            <a href="https://www.finn.no/bedriftskunde/jobb/nytt-om-jobbmarkedet/nye-priser-for-stillingsannonser-pa-finn-jobb-2024">Les mer her</a>
          </div>
        ) : (
          <div className="sm:pr-32">
            <p>Det skal lønne seg å bruke oss ofte, og derfor får du rabatt fra din ellevte bemanningsannonse. Volumrabatten beregnes ut fra det eksakte antall annonser som har blitt publisert - jo flere annonser du legger ut, desto lavere blir prisen. </p>
            <br />
            <p>Prisen er dynamisk og vil variere ut fra antall publiserte bemanningsannonser de siste 365 dagene.</p>
          </div>
        )}
      </Expandable>
      { adModelTab !== JOB_AGENCY_FULL_TIME && (
      <Expandable
        title={(
          <div className="font-bold">
            Effektpakker
          </div>
            )}
        className="my-8"
        box
        animated
      >
        <div className="sm:pr-32">
          <p>For å fremheve din annonse over andre annonser og nå flere kandidater, kan du kjøpe en effektpakke. Dette øker synligheten av din annonse på FINN, og avhengig av effektpakken du velger synliggjøres også annonsen i flere kanaler, f.eks. nettaviser og Facebook.</p>

          <a href="https://www.finn.no/bedriftskunde/jobb/produktene-vare/effekt-jobb">Les mer her</a>
        </div>
      </Expandable>
      )}
      <Expandable
        expanded={isLoggedIn}
        title={(
          <div className="font-bold">
            Prøv priskalkulatoren for å se priser
          </div>
            )}
        className="mt-8 mb-32"
        box
        animated
      >
        <div className="mb-24">
          <Toggle
            className="accent-white"
            type="radio"
            title="Hva slags stilling rekrutterer du til?"
            options={options}
            onChange={(selected) => { onAdTypeChange(selected.value); }}
            defaultSelected={[options[0]]}
            selected={selectedAdModelRadio}
          />
        </div>
        <div className="sm:pr-32">
          <p className="font-bold text-s">Hvor mange annonser har du lagt ut de siste 365 dagene?</p>
          <div className="lg:flex">
            <TextField
              className="lg:w-3/12"
              type="number"
              value={numberOfPublishedAdsInput.toString()}
              min="0"
              onChange={(event) => {
                const result = fixToPositiveIntegerString(event.target.value);
                setNumberOfPublishedAdsInput(result);
                if (result.length) setNumberOfPublishedAdsErrorMessage('');
              }}
              invalid={!!numberOfPublishedAdsErrorMessage}
              helpText={numberOfPublishedAdsErrorMessage}
            />
            {isLoggedIn && (
            <p className="text-s mt-14 ml-24 hidden lg:block">
              Du har lagt ut
              {' '}
              <p className="font-bold inline">
                {adModelNumberOfPublishedAds}
              </p>
              {' '}
              {jobAdText}
              {' '}
              de siste 365 dagene
            </p>
            )}
          </div>

          { showOccupationDropDown && (
          <div>
            <h5 className="pb-4 mt-24">Hva beskriver din stillingsannonse best?</h5>
            <div className="flex gap-x-8 gap-y-4 flex-wrap">
              {selectedOccupations.map((selectedOccupation) => (
                <Pill
                  className="text-m!"
                  label={selectedOccupation.description}
                  canClose
                  onClose={() => removeOccupation(selectedOccupation)}
                />
              ))}
            </div>
            <div className="flex sm:flex-row  flex-col space-x-14 items-end pt-12 sm:items-baseline lg:w-3/6">
              <Select
                className="flex-auto mb-16 sm:mb-0"
                hint="Velg inntil tre alternativer som beskriver stillingen"
                always
                invalid={occupationError}
                onChange={(event) => {
                  selectOccupation(event);
                  setOccupationError(false);
                }}
                value={selectedOccupations.length !== 0 ? selectedOccupations[selectedOccupations.length - 1].code : ''}
              >
                <option key="PLACEHOLDER" value="" aria-label="placeholder" />
                {occupations.map((occupation: Occupation) => (
                  <option key={occupation.id} value={occupation.id}>{occupation.description}</option>
                ))}
              </Select>
            </div>
          </div>
          ) }
          <Button loading={fetchingPrices} primary className="mt-16 h-48 p-8 whitespace-nowrap min-w-full sm:min-w-max" onClick={() => getJobPrices()}>Sjekk prisen</Button>
          {jobPrices && (
            <div>
              <table className="mt-28 w-full">
                <thead>
                  <tr className="border-b border-dashed text-left">
                    <th className="text-l">Basisannonse</th>
                    <th className="text-s pb-16  sm:pr-8 text-right">Pris*</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="pl-8 pt-14 align-top">
                      <p className="text-m font-bold whitespace-nowrap">{jobPrices.baseSpecification.heading}</p>
                      <ul>
                        <li className="text-s list-disc ml-20">{jobPrices.baseSpecification.description}</li>
                      </ul>
                    </td>

                    <td className="align-baseline pt-14 sm:pr-8 text-right">
                      <p className="text-m font-bold">
                        {jobPrices.baseSpecification.price.discounted}
                      </p>
                      <div className="text-s">
                        <p>
                          for annonse nr
                          {' '}
                          {priceCalcNextVolume}
                        </p>
                        { jobPrices.baseSpecification.price.discounted !== jobPrices.baseSpecification.price.full && (
                        <>
                          <p className="s-text-positive">
                            {jobPrices.baseSpecification.price.discount}
                            {' '}
                            volumrabatt
                          </p>
                          <p>
                            Ordinær pris
                            {' '}
                            <p className="line-through inline">{jobPrices.baseSpecification.price.full}</p>
                          </p>
                        </>
                        )}
                      </div>
                    </td>
                  </tr>
                  { jobPrices.upsellSpecifications && (
                  <>
                    <tr className="border-b border-dashed">
                      <th className="text-l text-left">Tilleggsvalg</th>
                    </tr>
                    { jobPrices.upsellSpecifications.map((specification) => (
                      <>
                        <tr>
                          <td className="pl-8 pt-14">
                            <p className="text-m font-bold whitespace-nowrap">{specification.heading}</p>
                          </td>

                          <td className="align-baseline pt-14 sm:pr-8 text-right">
                            {specification.price.full}
                          </td>
                        </tr>
                        <tr className="border-b border-dashed">
                          <td colSpan={3} className="pl-8">
                            <div className="flex flex-col mb-12">
                              <span className="text-s font-bold whitespace-nowrap">{specification.mainDescription}</span>
                              <ul>
                                {specification.subDescriptionPoints.map((subPoint) => (
                                  <li className="text-s list-disc ml-20">{subPoint}</li>
                                ))}
                              </ul>
                            </div>
                          </td>

                        </tr>
                      </>
                    ))}
                  </>
                  )}
                </tbody>
              </table>
              { jobPrices.upsellSpecifications && (
              <div className="pt-16">
                <p className="text-xs mb-0">*Velger du å kjøpe effektpakke ved publisering av annonsen eller på abonnement får du rabatt sammenlignet med å kjøpe en effektpakke i etterkant av publisering.</p>
                <p className="text-xs mb-0">Prisene du får oppgitt i priskalkulatoren er kun gyldige basert på de valgte variablene, på det tidspunktet det ble sjekket, ettersom prisene er dynamiske og vil variere over tid.</p>
                <p className="text-xs">Alle priser er eksklusiv merverdiavgift.</p>
              </div>
              )}
            </div>
          )}
        </div>
      </Expandable>
    </div>
  );
}

export default JobPriceOverview;
